import type { QueryClient } from '@tanstack/react-query';
import {
  eventsBaseKey,
  featuredEventsBaseKey,
  eventKey,
  resolveEventBaseKey,
} from '@lib/cache/events';
import { spaceEventsKey, spaceLiveProbeBaseKey, spaceLiveProbeKey } from '@lib/cache/spaces';
import {
  questionMySpacesFeedKey,
  questionsEventFeedBaseKey,
  questionsSpaceKey,
} from '@lib/cache/questions';
import {
  invalidateSpaceMembershipQueries,
  type SpaceMembershipChange,
} from '@utils/invalidateSpaceMembershipQueries';

/** What the shared sweep needs: which spaces the event touches, and what the attend did to the
 *  viewer's membership in them. Both attend pipelines can answer both questions. */
export interface GoingEventQueriesTarget {
  id: string;
  /** Every space this event touches (primary + approved cross-posted), deduped. Callers holding
   *  an `EventRead` build it with `eventSpaceIds` (@utils/eventSpaceIds); callers holding an attend response pass the
   *  response's space ids straight through. Need not cover `membershipChange`'s ids: this file
   *  unions the two before any per-space loop, so a space named only by the membership outcome
   *  still gets its question feed, event list and live probe swept. */
  spaceIds: string[];
  /** What this attend did to the viewer's membership, as `invalidateSpaceMembershipQueries`'s own
   *  union — carried through rather than translated from a nullable id list, so a producer cannot
   *  collapse "cannot tell" into "provably none" the way `results?.map(...) ?? []` used to.
   *  `{ kind: 'none' }`: provably nothing moved (the un-going DELETE never leaves a space; an
   *  un-retried attend that lands entirely in `skipped` / `failed`). `{ kind: 'these', ids }`:
   *  provably those and no others (an un-retried response's `joined` + `pending`). `{ kind:
   *  'unknown', candidateIds }`: the caller cannot tell — an errored POST may still have committed
   *  (a timeout after commit looks like a rejection), a body may be unreadable, or the invocation
   *  RETRIED, which lets attempt 1's committed join hide behind attempt 2's `skipped` — falling
   *  back to every space it could name. */
  membershipChange: SpaceMembershipChange;
}

/**
 * Refreshes every cache a "going" (attend) change can affect (design §4.1). Shared by
 * `useToggleGoingEvent`'s `onSettled` (in-app toggle) and `executePostAuthIntent`'s
 * `executeGoingEvent` (post-auth attend) so the two attend pipelines cannot drift out of sync
 * again — the missing `spaceLiveProbeBaseKey` sweep on the post-auth path (stale native "Join
 * event" CTA) and the `spaceMembersKey` roster sweep that ran at only one call site were both
 * that kind of drift. Anything an attend can change belongs in here, not at one call site.
 *
 * Event lists, question feeds and live probes are swept for every affected space: they all carry
 * this event's going state, which both toggle directions change. Everything membership moves is
 * delegated to `invalidateSpaceMembershipQueries`, shared with the other pipeline that auto-joins
 * spaces server-side (`useShareQuestionToSpaces`) so the two cannot drift apart again — they did,
 * and it cost the share path its community-list and My Calendars sweeps (#1054). That helper
 * rides a narrower predicate than the rest of this file, since only the attend POST can change
 * membership; the reasoning lives with it.
 *
 * Invalidation-only by design (mirrors `useToggleSaveEvent`); any optimistic flip/rollback is the
 * caller's concern.
 */
export function invalidateGoingEventQueries(
  queryClient: QueryClient,
  event: GoingEventQueriesTarget,
): void {
  // Every space touched by EITHER input, deduped ONCE here and used by every per-space loop below.
  //
  // The union is what makes `spaceEventLists: true` below honest. That flag promises the helper
  // that this caller's per-space sweep COVERS every space it hands over, and `spaceIds` alone does
  // not: a client whose `linked_space_ids` predate a newly approved cross-post holds a stale space
  // list, so the response can name a space in `joined` that `spaceIds` never had. Under a plain
  // `new Set(event.spaceIds)` that space lost its event list (the flag silenced the helper's copy),
  // its question feed and its live probe. Unioning fixes all three at once and costs nothing in the
  // normal case, where the membership ids are already a subset.
  //
  // Deduping is separately load-bearing: `executePostAuthIntent` builds `spaceIds` by concatenating
  // the attend response's four buckets with no `Set`, so a space landing in two buckets would reach
  // three loops twice over — the question feed, the space event list and the live probe — each
  // duplicate cancelling its own in-flight refetch and issuing another. Unreachable today, since
  // `AttendEventResponse`'s buckets are documented mutually exclusive
  // (src/models/api/event.ts:222-226); the point is that the invariant is not trusted at ANY id
  // loop in this pair rather than at one of them, so the next reader does not have to work out
  // which boundary is the guarded one. `invalidateSpaceMembershipQueries` defends its own input
  // the same way, for its second caller's sake.
  const membershipSpaceIds =
    event.membershipChange.kind === 'these'
      ? event.membershipChange.ids
      : event.membershipChange.kind === 'unknown'
        ? event.membershipChange.candidateIds
        : [];
  const affectedSpaceIds = [...new Set([...event.spaceIds, ...membershipSpaceIds])];

  void queryClient.invalidateQueries({ queryKey: eventsBaseKey() });
  void queryClient.invalidateQueries({ queryKey: featuredEventsBaseKey() });
  void queryClient.invalidateQueries({ queryKey: eventKey(event.id) });
  // The authed page renders is_going / going_count / preview_users from the slug-resolved
  // event cache (resolveEventKey), a separate root from eventKey(id); invalidate it so the
  // who's-going summary + count reflect this toggle (that summary has no optimistic layer).
  void queryClient.invalidateQueries({ queryKey: resolveEventBaseKey() });
  // The authed event page's presence feed (questions authored by the people who are Going)
  // lives under the ['questions', 'event-feed', id] root, which eventKey(id) above does NOT
  // reach. A Going toggle changes the going-set, which changes the feed authorship, so refresh
  // every sort in one call. (The roster needs nothing extra: eventAttendeesKey nests under
  // ['event', id, ...], so the eventKey(id) invalidation above already sweeps it by prefix.)
  void queryClient.invalidateQueries({ queryKey: questionsEventFeedBaseKey(event.id) });
  // `author_live_event` — the "at {event}" chip on the author's posts — is baked into the
  // question-feed caches, and a Going flip changes it both ways (un-going must clear the
  // chip; Dexter field test W2-8). Refresh every space feed that can carry it: the primary
  // space plus approved cross-posted (linked) spaces, deduped. These feeds are dependent
  // data, not the list this mutation writes, so settle-time invalidation is the correct
  // sync (not a TECH-305 refetch-what-you-wrote case).
  for (const affectedSpaceId of affectedSpaceIds) {
    void queryClient.invalidateQueries({ queryKey: questionsSpaceKey(affectedSpaceId) });
    void queryClient.invalidateQueries({ queryKey: spaceEventsKey(affectedSpaceId) });
  }
  // A Space that lists this event without hosting it names no id above, yet its calendar (the
  // `'listed'` flavour of `spaceEventsKey`) carries this event's Going pill. A Space the loop above
  // already swept is skipped: its prefix invalidation reached the listed key too, and a second one
  // would cancel that refetch and start another.
  void queryClient.invalidateQueries({
    predicate: query =>
      query.queryKey[0] === 'spaceEvents' &&
      query.queryKey.at(-1) === 'listed' &&
      !affectedSpaceIds.some(spaceId => spaceId === query.queryKey[1]),
  });
  // Home my-spaces feed: same chip, home surface — covered now so a server-side home-feed
  // chip needs no second invalidation pass later. Unconditional, because the chip moves on every
  // settle in both directions. It is also the membership reason's coverage (a join adds a whole
  // space's questions to this feed), which is exactly why the shared helper below does NOT sweep
  // it a second time: a duplicate invalidation of an already-loaded query cancels this one's
  // in-flight request and starts another, so the second call costs a round-trip and buys nothing.
  void queryClient.invalidateQueries({ queryKey: questionMySpacesFeedKey() });
  // The space-page live probe caches this event's `is_going` too, and its root is
  // DELIBERATELY outside every family swept above (spaceLiveProbeKey's docstring:
  // nesting it under spaceEventsKey would get it corrupted by the approval
  // mutation's InfiniteData patcher). Without this line a probe-sourced slot
  // holds the pre-toggle going state for up to railStaleTimeMs. Every direction and outcome
  // moves `is_going`, so this branch keys only on whether the caller could name the spaces.
  if (affectedSpaceIds.length > 0) {
    for (const affectedSpaceId of affectedSpaceIds) {
      void queryClient.invalidateQueries({ queryKey: spaceLiveProbeKey(affectedSpaceId) });
    }
  } else {
    // Id-less caller: sweep the probe root rather than strand a stale native "Join event" CTA
    // in a space the caller couldn't name.
    void queryClient.invalidateQueries({ queryKey: spaceLiveProbeBaseKey() });
  }
  // Membership rides a narrower predicate than everything above. Going is not just an RSVP (the
  // attend POST auto-joins the event's spaces server-side), but un-going only removes the RSVP and
  // never leaves a space, and a `skipped` / `failed` row changed no membership ON THE ATTEMPT THAT
  // REPORTED IT. That last qualifier is the whole subtlety, and it is why the producers hand over
  // a union rather than a bare id list: `skipped` also covers "already a member", so on a RETRIED
  // invocation attempt 1's committed join is indistinguishable from a space the viewer already
  // belonged to. Each producer therefore decides which of the three cases it can honestly claim
  // (`useToggleGoingEvent`'s `membershipChangeOf` widens to `unknown` on a retry;
  // `executePostAuthIntent` cannot retry and says so), and this file just carries the verdict.
  // Everything downstream of it — the rosters, the space event lists, the community list, community
  // detail, my-joined — belongs to the shared helper, which the share pipeline calls with the same
  // union. The three keys the helper does NOT sweep for this caller (the my-spaces feed and the
  // space event lists above, and My Calendars via the flag below) are already covered
  // unconditionally here, and a second invalidation of any of them would cost a
  // cancelled-and-restarted refetch, not a free deduped one.
  //
  // Nothing else in a `CommunityRead` needs a wider gate: it carries no going state and no
  // embedded events, and `activation_progress` is a stored JSONB column whose only writer is the
  // admin-only sub-step POST (backend `crud/space.complete_activation_substep`), never an attend.
  invalidateSpaceMembershipQueries(queryClient, {
    change: event.membershipChange,
    alreadySweptByCaller: {
      // `eventsBaseKey()` at the top of this function is a prefix of every My Calendars and
      // live-rail entry, so the helper must not sweep them again: for an already-loaded query that
      // second invalidation cancels the refetch this function just started and issues another.
      eventRoot: true,
      // `spaceEventsKey` is swept above for every affected space, and `affectedSpaceIds` is the
      // UNION of `spaceIds` and the membership outcome's own ids — so the interface's COVERS
      // requirement holds by construction here, not by an argument about where the two lists come
      // from. That matters because the argument had a hole: a client holding an event whose
      // `linked_space_ids` predate a newly approved cross-post can be handed a `joined` space that
      // `spaceIds` never contained, and this flag would then have silenced the helper's only sweep
      // of that space's event list. The union closes it (see the comment on `affectedSpaceIds`).
      spaceEventLists: true,
      // `questionMySpacesFeedKey()` is swept unconditionally above for the "at {event}" chip.
      mySpacesFeed: true,
      // No post page is swept by this function, so the helper's sweep of them stands.
      heldPostPages: false,
    },
  });
}
